import { useCallback, useEffect, useRef, useState } from 'react';

const audioConstraints = {
	echoCancellation: true,
	noiseSuppression: true,
};

const formatAudioMimeType = () => {
	const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
	return types.find(type => MediaRecorder.isTypeSupported(type));
};

const AudioRecorder = ({ children, onError }) => {
	const audioChunksRef = useRef([]);
	const mediaRecorderRef = useRef(null);
	const streamRef = useRef(null);
	const [isRecording, setIsRecording] = useState(false);
	const [mediaRecorder, setMediaRecorder] = useState(null);
	const [recordingBlob, setRecordingBlob] = useState(null);
	const [stream, setStream] = useState(null);

	const cleanupStream = useCallback(() => {
		streamRef.current?.getTracks().forEach(track => track.stop());
		streamRef.current = null;
		setStream(null);
	}, []);

	const startRecording = useCallback(async () => {
		try {
			audioChunksRef.current = [];
			setRecordingBlob(null);

			const nextStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
			const mimeType = formatAudioMimeType();
			const nextMediaRecorder = mimeType ? new MediaRecorder(nextStream, { mimeType }) : new MediaRecorder(nextStream);

			nextMediaRecorder.ondataavailable = event => {
				if (event.data.size > 0) audioChunksRef.current.push(event.data);
			};

			nextMediaRecorder.onstop = () => {
				setRecordingBlob(new Blob(audioChunksRef.current, { type: nextMediaRecorder.mimeType }));
			};

			streamRef.current = nextStream;
			mediaRecorderRef.current = nextMediaRecorder;
			setStream(nextStream);
			setMediaRecorder(nextMediaRecorder);
			nextMediaRecorder.start();
			setIsRecording(true);
		} catch (e) {
			onError?.(e);
		}
	}, [onError]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current.stop();
		cleanupStream();
		setIsRecording(false);
	}, [cleanupStream]);

	const cancelRecording = useCallback(() => {
		audioChunksRef.current = [];
		if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current.stop();
		cleanupStream();
		setIsRecording(false);
		setRecordingBlob(null);
	}, [cleanupStream]);

	useEffect(() => cleanupStream, [cleanupStream]);

	return children({
		cancelRecording,
		isRecording,
		mediaRecorder,
		recordingBlob,
		startRecording,
		stopRecording,
		stream,
	});
};

export { AudioRecorder };
